import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { TaskEntity, TaskTagEntity, UserEntity } from '@core/entities';
import { ApprovedStatus, NotificationType, SocketEvents, SubtaskStatus, TaskStatus, TaskType, UpdateTabType } from '@core/enums';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { EnumToTabsSelectionArray, getAlertTypeByApprovedStatusOfEntity, GetUserId, isMobileView, updateObjectProperties } from '@core/utils';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { DefaultImageDirective, FetchProfileImageDirective } from '@shared/directives';
import { AlertMessageComponent, BadgeComponent, ButtonComponent, DynamicTabsComponent, IconComponent, TabDirective, TabsSelectionComponent } from '@shared/components/ui';
import { SubtaskFormComponent, TasksFormComponent } from '@shared/components/forms';
import { TasksComposition } from '@shared/compositions';
import { FriendlyDatePipe, HumanizePipe, TruncateTextPipe } from '@shared/pipes';
import { TippyModule } from '@ngneat/helipopper';

@Component({
  selector: 'app-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss'],
  imports: [
    IconComponent,
    BadgeComponent,
    TranslateModule,
    DynamicTabsComponent,
    TabDirective,
    DatePipe,
    FormsModule,
    NgxSkeletonLoaderModule,
    DefaultImageDirective,
    FetchProfileImageDirective,
    AlertMessageComponent,
    SlicePipe,
    FriendlyDatePipe,
    TippyModule,
    TruncateTextPipe,
    HumanizePipe,
    TabsSelectionComponent,
    NgTemplateOutlet,
    ButtonComponent,
  ],
})
export class TaskDetailsComponent extends TasksComposition implements OnInit, AfterViewInit {
  subTasks: any;
  // Dummy Messages
  messages: any[] = [];
  messageInput = '';
  dialogRef: any;
  dialogService: any;
  isVisible: false;
  expandedSubtaskIndex: number | null = null;
  @ViewChild('tabContent') tabContent: ElementRef;
  protected readonly TasksType = TaskType;

  protected readonly TaskStatus = TaskStatus;

  protected readonly isMobileView = isMobileView();
  protected readonly GetUserId = GetUserId;
  protected readonly SubtaskStatus = SubtaskStatus;
  protected readonly ApprovedStatus = ApprovedStatus;
  @ViewChild('chatPanel')
  private readonly _myScrollContainer!: ElementRef;
  private _teamMembers: UserEntity[];

  constructor() {
    super();
    this.isLoading = true;
    if (!isMobileView()) {
      this.dialogService = inject(DialogService);
      this.dialogRef = inject(DialogRef);
      this.currentTask = this.dialogRef.data.task;
      this.currentTaskStatus = this.dialogRef.data.currentTaskStatus;
    } else {
      this.currentTask = this._bottomSheetService.currentSheetData.task;
      this.currentTaskStatus = this._bottomSheetService.currentSheetData.currentTaskStatus;
    }

    if (this.currentTask && this.currentTask.id) {
      this._fetchTaskDetails();

      this._socketService
        .fromEvent(SocketEvents.GetNotification)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (data: NotificationType) => {
            if (data === NotificationType.Task) {
              this._fetchTaskDetails();
            }
          },
        });
    }
  }

  get taskTags(): TaskTagEntity[] {
    return this.currentTask?.tags as TaskTagEntity[];
  }

  get alertType(): any {
    this.currentTask.approvedStatus =
      this.currentTask.status === 0 && !this.currentTask.denyById
        ? ApprovedStatus.Approved
        : this.currentTask.status === ApprovedStatus.Pending && !this.currentTask.denyById
          ? ApprovedStatus.Pending
          : this.currentTask.status === ApprovedStatus.Pending && this.currentTask.denyById
            ? ApprovedStatus.Rejected
            : ApprovedStatus.Approved;
    return getAlertTypeByApprovedStatusOfEntity(this.currentTask);
  }

  /**
   * Determines if admin actions should be displayed
   */
  shouldShowAdminActions(): boolean {
    return this.showUpdatedRecord || this.currentTask?.status === ApprovedStatus.Pending;
  }

  /**
   * Determines the approval type based on current state
   */
  getApprovalType(): boolean {
    return this.showUpdatedRecord;
  }

  /**
   * Determines if the complete action should be shown for mobile users
   */
  shouldShowCompleteAction(): boolean {
    return this.currentTask?.status !== SubtaskStatus.Complete && this.currentTask?.status !== ApprovedStatus.Pending;
  }

  getParticipantName(participantId: number): string {
    const member = this._teamMembers.find((member) => member.id === participantId);
    if (!member) return 'Deleted User';
    return member?.fullName || member?.firstName + ' ' + member?.lastName;
  }

  ngOnInit(): void {
    this._fetchTeam();
    // Update tabs is just required in details page
    this.updateSelectionTabs = EnumToTabsSelectionArray(UpdateTabType, this._translateService);
  }

  ngAfterViewInit() {
    if (this.tabContent) {
      this.scrollToBottom();
    }
  }

  scrollToBottom(): void {
    try {
      setTimeout(() => {
        this._myScrollContainer.nativeElement.scrollTop = this._myScrollContainer?.nativeElement?.scrollHeight;
      });
    } catch (err) {
      console.log('[ScrollToBottom]:' + err);
    }
  }

  onSend() {
    this.messages.push({
      text: this.messageInput,
      isMine: true,
      sentAt: new Date(),
      user: { id: 2, username: 'Bob' },
    });
    this.messageInput = '';
    this.scrollToBottom();
  }

  close(): void {
    if (!isMobileView()) {
      this._dialogService.closeAll();
    } else {
      this._bottomSheetService.close();
    }
  }

  openTaskBottomSheet(task: TaskEntity): void {
    if (!isMobileView()) {
      this.dialogService.open(TasksFormComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-y-auto',
        data: {
          task,
          subTasksListOfATask: task.subtasks,
        },
      });
    } else {
      this._bottomSheetService.open(TasksFormComponent, {
        height: '95%',
        // padding: '16px',
        showCloseButton: false,
        data: {
          task,
          subTasksListOfATask: task.subtasks,
        },
      });
    }
  }

  openSubtaskBottomSheet(): void {
    this._bottomSheetService.open(SubtaskFormComponent, {
      height: '45%',
      showCloseButton: false,
      data: {
        task: this.currentTask,
      },
    });
  }

  toggleSubTaskDetails(index: number) {
    if (this.expandedSubtaskIndex === index) {
      this.expandedSubtaskIndex = null;
    } else {
      this.expandedSubtaskIndex = index;
    }
    this.updateArrowState();
  }

  navigateToGroupDetails(groupId: number): void {
    const route = `/m/groups/${groupId}/details/`;
    this._router.navigate([route], {
      queryParams: { source: isMobileView() ? 'tasks' : undefined },
    });
    this.close();
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  updateArrowState(): void {
    this.currentTask.subtasks.forEach((subTask: any, idx: number) => {
      subTask.isArrowUp = idx === this.expandedSubtaskIndex;
    });
  }

  private _fetchTaskDetails() {
    this._fetchTaskByIdReturnPromise(this.currentTask.id)
      .then((task) => {
        this.currentTask = task;
        this.currentSubtasks = task.subtasks;

        if (this.currentTask && this.currentTask.subtasks && this.currentTask.subtasks.length > 0) {
          this.currentTask.subtasks = this.currentTask?.subtasks.map((subTask) => ({
            ...subTask,
            isVisible: false,
            isArrowUp: false,
          }));
        }

        this.originalRecord = this.currentTask;
        if (this.originalRecord?.updatedRecord) {
          this.updatedRecord = updateObjectProperties(this.originalRecord, this.originalRecord.updatedRecord);
        }

        if (this.updatedRecord) {
          this.currentUpdatedRecordTab = this._permissionService.isAdmin() ? UpdateTabType.UPDATE : UpdateTabType.CURRENT;
        }

        this._fetchCourseTaskCollaborators();
        this._fetchTeamUsersAndUpdateSubtasksParticipants();
      })
      .catch((error) => {
        this._toast.error(this.error);
        if (isMobileView()) {
          this._router.navigate(['/m/tasks']);
        } else {
          this.dialogService?.closeAll();
        }
      });
  }

  private _fetchTeam() {
    this._useClub.getClubMembers().then((teamMembers) => {
      this._teamMembers = teamMembers;
    });
  }
}
